// Limits for the vitals on the prescription pad, shared by the boxes (VitalsForm) and the save check
// (usePrescriptionForm).

// ─── Extreme value thresholds ─────────────────────────────────────────────────
// These prompt a "please verify" — they don't block input.
// Set wide enough to cover all patient types: neonates, elderly, critically ill.
export const EXTREMES = {
  BP_SYS:  { min: 50,   max: 280  },
  BP_DIA:  { min: 20,   max: 160  },
  pulse:   { min: 30,   max: 280  },
  spo2:    { min: 60,   max: 100  },
  weight:  { min: 0.3,  max: 450  }, // premature neonates to morbidly obese
  temp:    { min: 85,   max: 113  }, // hypothermia to hyperthermia (°F)
};

// What a box accepts at all: the most a reading can physically be (and the server can store), and how many
// decimals it keeps. Anything past these is a typing slip, so the box doesn't take the key.
export const INPUT_LIMITS = {
  BP_SYS: { max: 300, decimals: 0 },
  BP_DIA: { max: 200, decimals: 0 },
  pulse:  { max: 300, decimals: 0 },
  spo2:   { max: 100, decimals: 0 },
  weight: { max: 500, decimals: 2 },
  temp:   { max: 115, decimals: 1 },
};

/** Whether a typed value fits a box's limits ("" always does) */
export function fitsLimit(value: string, limit: { max: number; decimals: number }): boolean {
  if (value === "") return true;
  const n = parseFloat(value);
  if (isNaN(n) || n < 0 || n > limit.max) return false;
  const decimals = value.split(".")[1]?.length ?? 0;
  return limit.decimals === 0 ? !value.includes(".") : decimals <= limit.decimals;
}

/** A temperature typed in °C (30–45) as °F, one decimal; null when it isn't one */
export function celsiusToFahrenheit(value: string): string | null {
  const n = parseFloat(value);
  if (isNaN(n) || n < 30 || n > 45) return null;
  return String(Math.round((n * 9 / 5 + 32) * 10) / 10);
}

type VitalValue = string | number | null | undefined;

/** Vitals outside what a patient can have, as lines for the "Save anyway?" check */
export function implausibleVitals(vitals: { bp?: VitalValue; pulse?: VitalValue; spo2?: VitalValue; weight?: VitalValue; temp?: VitalValue } | undefined): string[] {
  if (!vitals) return [];
  const out: string[] = [];
  const check = (label: string, value: VitalValue, range: { min: number; max: number }, unit: string) => {
    const n = parseFloat(String(value ?? ""));
    if (!isNaN(n) && (n < range.min || n > range.max)) out.push(`• ${label} ${value}${unit}`);
  };
  const [sys, dia] = String(vitals.bp ?? "").split("/");
  check("BP systolic", sys, EXTREMES.BP_SYS, " mmHg");
  check("BP diastolic", dia, EXTREMES.BP_DIA, " mmHg");
  check("Pulse", vitals.pulse, EXTREMES.pulse, " bpm");
  check("SpO₂", vitals.spo2, EXTREMES.spo2, "%");
  check("Temperature", vitals.temp, EXTREMES.temp, " °F");
  check("Weight", vitals.weight, EXTREMES.weight, " kg");
  return out;
}
